import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { connectAdtWithTicket } from "@/api/target-connection";

/**
 * Onde o retorno do login ADT aterrissa (S-070).
 *
 * O SAP só devolve o reentrance ticket para `localhost`, e o myGROW roda em servidor. A
 * extensão adt-connect intercepta esse retorno e o encaminha para cá, preservando `p`,
 * `state` e `reentrance-ticket`. Esta página só entrega o que recebeu.
 *
 * 🔶 **Fora do AuthGuard de propósito.** O ticket tem uso único e vida curta (medida
 * acima de 90 segundos); se uma sessão expirada do myGROW redirecionasse para o login,
 * ele se perderia e o dev teria de refazer o SSO. Quem autentica a entrega é o `state`
 * assinado, conferido no servidor contra projeto, usuário e tenant.
 */
export default function AdtConnected() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get("p") ?? "";
  const state = params.get("state") ?? "";
  const ticket = params.get("reentrance-ticket") ?? "";
  // A entrega já aconteceu nesta URL: é o que sobra depois de tirar a credencial dela.
  const alreadyDelivered = params.get("done") === "1";

  const [status, setStatus] = useState<"delivering" | "ok" | "error">(
    alreadyDelivered ? "ok" : "delivering",
  );
  const [message, setMessage] = useState("");
  const [responsible, setResponsible] = useState("");
  // O ticket é single-use: entregar duas vezes faria a segunda falhar e trocaria um
  // sucesso por uma mensagem de erro, que é o pior desfecho possível aqui.
  const delivered = useRef(false);

  useEffect(() => {
    if (delivered.current || alreadyDelivered) return;
    if (!projectId || !state || !ticket) {
      setStatus("error");
      setMessage(
        "O retorno do SAP chegou incompleto. Reinicie a conexão pelo myGROW.",
      );
      return;
    }
    delivered.current = true;
    // Tira `state` e `reentrance-ticket` da URL antes de qualquer coisa. Eles ficariam
    // na entrada de sessão da aba e num reload, e o `state` é a credencial desta rota:
    // o `verify_state` confere assinatura, `exp` e `purpose`, sem consumo, então dentro
    // dos 10 minutos quem tiver a URL entrega um ticket próprio nesta sessão.
    //
    // Pelo router e não por `window.history`, para o teste conseguir afirmar.
    setParams(projectId ? { p: projectId } : {}, { replace: true });
    connectAdtWithTicket(projectId, { state, ticket })
      .then((r) => {
        setResponsible(r.responsible);
        setStatus("ok");
        // ✅ **Marca de entrega, depois da confirmação do servidor.** Sem ela um F5 nesta
        // aba não distingue "nunca teve credencial" de "já entreguei", e a tela mandava
        // refazer um SSO que funcionou. Só depois do `then`: marcar antes faria o F5 de
        // uma entrega que falhou mostrar sucesso.
        setParams(projectId ? { p: projectId, done: "1" } : { done: "1" }, {
          replace: true,
        });
      })
      .catch((e) => {
        // `detail` nem sempre é string: num 422 o FastAPI devolve uma LISTA DE OBJETOS,
        // e renderizar isso como child estoura o React e deixa a tela branca, na única
        // página que existe para dizer ao dev o que deu errado.
        const detail = e?.response?.data?.detail;
        setStatus("error");
        setMessage(
          typeof detail === "string" && detail
            ? detail
            : "Não foi possível conectar. Reinicie a conexão pelo myGROW.",
        );
      });
  }, [projectId, state, ticket, alreadyDelivered]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="max-w-md rounded-lg border bg-background p-8 text-center">
        {status === "delivering" && (
          <p className="text-muted-foreground">Conectando ao SAP...</p>
        )}

        {status === "ok" && responsible && (
          <>
            <h1 className="mb-2 text-xl font-semibold">Conectado ao SAP</h1>
            <p className="mb-4 text-sm text-muted-foreground">
              Você está autenticado como <strong>{responsible}</strong>. Pode
              voltar e disparar o desenvolvimento.
            </p>
            {projectId && (
              <Link
                to={`/projects/${projectId}`}
                className="text-primary underline hover:text-primary/90"
              >
                Voltar ao projeto
              </Link>
            )}
          </>
        )}

        {/* Recarregou a página. A marca na URL conta que a entrega aconteceu nesta aba,
            e nada além disso: a sessão dura ~30 min e pode ter expirado desde então,
            então afirmar "conectado" aqui seria afirmar o que não se sabe. Quem sabe o
            estado atual é a tela do projeto, que lê o status. */}
        {status === "ok" && !responsible && (
          <div data-testid="adt-entregue">
            <h1 className="mb-2 text-xl font-semibold">Login entregue</h1>
            <p className="mb-4 text-sm text-muted-foreground">
              O retorno do SAP já foi entregue nesta aba. O estado atual da
              sessão aparece na tela do projeto.
            </p>
            {projectId && (
              <Link
                to={`/projects/${projectId}`}
                className="text-primary underline hover:text-primary/90"
              >
                Voltar ao projeto
              </Link>
            )}
          </div>
        )}

        {status === "error" && (
          <div data-testid="adt-erro">
            <h1 className="mb-2 text-xl font-semibold">
              Não deu para conectar
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">{message}</p>
            {projectId && (
              <Link
                to={`/projects/${projectId}`}
                className="text-primary underline hover:text-primary/90"
              >
                Voltar ao projeto
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
